import React from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { GridToolbarQuickFilter, GridToolbarContainer } from '@mui/x-data-grid';
import { NewDatePicker } from '../../custom-widgets';
import StyledExportButton from './StyledExportButton';
import ToolbarColumnsButton from './ToolbarColumnsButton';
import ToolbarDateRangePicker from './ToolbarDateRangePicker';

const BaseToolbarSx = {
    padding: (theme) => theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    '& .MuiInputBase-root': { height: '48px' },
    '& .MuiFormControl-root': { height: '48px' },
};
const ActionButtonSx = {
    marginLeft: (theme) => theme.spacing(2),
    height: '48px',
};
const RightToolbarItem = {
    marginLeft: 'auto',
};

/**
 * Toolbar wrapper styling for custom toolbar components
 * @param {object} props
 * @param {React.ReactElement} props.children
 * @returns {React.ReactElement}
 */
export const StyledToolbar = ({ children }) => {
    return <GridToolbarContainer sx={BaseToolbarSx}>{children}</GridToolbarContainer>;
};

/**
 * @typedef {Object} DateHandlerProps
 * @property {boolean} enabled - whether or not the date picker should be displayed
 * @property {Date} date - the currently selected date
 * @property {Date} startDate - the earliest date that can be selected
 * @property {function} handleDateChange - function to call when the date is changed
 * @property {boolean} disablePastDate - whether or not dates before the current date should be disabled
 */

/**
 * @typedef {Object} TitleProps
 * @property {boolean} enabled - whether or not the title should be displayed
 * @property {string} text - the text to display
 * @property {boolean} inLine - whether or not the title should be displayed in line with the other toolbar items
 */

/**
 *  Default toolbar for a Table
 * @param {object} props
 * @param {DateHandlerProps} props.dateHandler - object containing props for the date picker
 * @param {TitleProps} props.title - object containing props for the title
 * @param {boolean} props.searchable - whether or not the search bar should be displayed
 * @param {boolean} props.exportable - whether or not the export button should be displayed
 * @param {string} props.resource - then name of the resource to add
 * @param {function} props.handleAdd - function to call when the add button is clicked
 * @param {object[]} props.actions  - array of objects containing props for the action buttons
 * @param {object} props.exportOptions - object containing props for the export button
 *
 * In order to use this inside a Table, you need to also pass `componentProps={{ toolbar: toolbarProps }}` or `toolbarProps={toolbarProps}` to the Table
 * where `toolbarProps` contains props for this component
 * @see https://v5.mui.com/x/api/data-grid/data-grid/
 * @see https://stackoverflow.com/questions/66446880/material-ui-data-grid-how-pass-parameter-to-custom-toolbar
 */

const DefaultToolbar = ({
    dateHandler = { enabled: false },
    dateRangeHandler = { enabled: false },
    setDefaultAllocations = { enabled: false },
    title = { enabled: false },
    searchable = true,
    exportable = true,
    resource,
    handleAdd,
    actions,
    exportOptions,
    columns = false,
    toolbarSx,
    customControls,
    datadogPrivacy = 'allow', // wrap
}) => (
    <GridToolbarContainer sx={{ ...BaseToolbarSx, ...toolbarSx }}>
        {title.enabled && (
            <Grid item xs={title.inLine ? 'auto' : 12} sx={{ ...title.sx }}>
                <Typography variant="h6">{title.text}</Typography>
            </Grid>
        )}
        {dateHandler.enabled && !dateHandler.loading && (
            <Grid item>
                <NewDatePicker
                    label="Month"
                    startDate={dateHandler.startDate ?? undefined}
                    selectedDate={dateHandler.date}
                    setSelectedDate={dateHandler.handleDateChange}
                    disablePastDate={dateHandler.disablePastDate}
                    disableFutureDate={dateHandler.disableFutureDate}
                    // wrap
                />
            </Grid>
        )}
        {dateHandler.enabled && dateHandler.loading && (
            <Grid item>
                <Grid container sx={{ paddingX: '64px' }}>
                    <Grid item xs={12}>
                        <Typography color="rgba(0, 0, 0, 0.54)" variant="caption">
                            Select Date
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ borderBottom: '1px solid black' }}>
                        <Typography variant="body1">{dateHandler.date.format('MMMM YYYY')}</Typography>
                    </Grid>
                </Grid>
            </Grid>
        )}
        {dateRangeHandler.enabled && <ToolbarDateRangePicker {...dateRangeHandler} />}
        {searchable && (
            <Grid item>
                <GridToolbarQuickFilter variant="outlined" debounceMs={searchable.debounceMs} data-dd-privacy={datadogPrivacy} />
            </Grid>
        )}
        <Grid item sx={RightToolbarItem}>
            {setDefaultAllocations.enabled && (
                <Button variant="contained" color="primary" sx={ActionButtonSx} onClick={setDefaultAllocations.handleSetDefaultAllocations}>
                    Set Default Allocations
                </Button>
            )}
            {customControls && <>{customControls}</>}
            {columns && <ToolbarColumnsButton />}
            {exportable && <StyledExportButton {...exportOptions} />}
            {/* TODO this should probably be pulled into actions */}
            {!!handleAdd && (
                <Button variant="contained" color="primary" sx={ActionButtonSx} onClick={handleAdd}>
                    + Add {resource}
                </Button>
            )}
            {actions?.length > 0 &&
                actions?.map((action) => {
                    if (action.shouldRender === false) return null;
                    return (
                        <Button key={`button_action.${action.title}`} variant={action.variant ?? 'contained'} color={action.color ?? 'primary'} sx={action.sx ?? ActionButtonSx} onClick={action.onClick} disabled={action.disabled} {...action.buttonProps}>
                            {!!action.fullTitle ? (
                                action.fullTitle
                            ) : (
                                <>
                                    {action.title} {resource}
                                    {action.pluralize ? 's' : ''} {action.icon && <>&ensp;{action.icon}</>}
                                </>
                            )}
                        </Button>
                    );
                })}
        </Grid>
    </GridToolbarContainer>
);

export default DefaultToolbar;
